/**
 * Карантин по площади (волна 2б, журнал №666–672): чистые функции подписей и
 * очереди решений. Без UI — тестируются отдельно.
 *
 * Правила, которые здесь держатся словами:
 * * гектары — земля под сигналом (поражённая часть), а не земля контуров;
 * * класс вида: красный только у «карантинного объекта» — у остальных класс
 *   подписан нейтрально;
 * * «обработали, ждём контроля» ≠ «устранено»: устранено только по замеру.
 */
import type { QuarantineStatus } from '../../data/types';
import type { WatchRow } from '../quarantineView';
import { defineMessages } from '../../../i18n/define';
import { tr , formatNum } from '../../../i18n/runtime';
import { lazyWords } from '../../lib/lazyWords';

const m = defineMessages({
  longA1: { id: 'quarantine.area.classLong.A1', defaultMessage: 'карантинный объект (A1, в РУз не встречается)', description: 'Класс вида (подробно): A1 — карантинный объект, в Узбекистане не встречается; строчными, в строке карточки' },
  longA2: { id: 'quarantine.area.classLong.A2', defaultMessage: 'карантинный объект (A2, есть в отдельных регионах)', description: 'Класс вида (подробно): A2 — карантинный объект, есть в отдельных регионах; строчными, в строке карточки' },
  longA3: { id: 'quarantine.area.classLong.A3', defaultMessage: 'некарантинный вредный организм (A3)', description: 'Класс вида (подробно): A3 — некарантинный вредный организм; строчными' },
  longQuarantine: { id: 'quarantine.area.classLong.quarantine', defaultMessage: 'карантинный объект', description: 'Класс вида (подробно): карантинный объект без уточнения категории; строчными' },
  longHarmful: { id: 'quarantine.area.classLong.harmful', defaultMessage: 'прочий вредный организм', description: 'Класс вида (подробно): прочий вредный организм (не карантинный); строчными' },
  longUnspecified: { id: 'quarantine.area.classLong.unspecified', defaultMessage: 'класс не указан', description: 'Класс вида (подробно): класс не указан; строчными' },
  shortA1: { id: 'quarantine.area.classShort.A1', defaultMessage: 'карантинный A1', description: 'Класс вида (кратко): карантинный объект категории A1; бейдж, очень узко' },
  shortA2: { id: 'quarantine.area.classShort.A2', defaultMessage: 'карантинный A2', description: 'Класс вида (кратко): карантинный объект категории A2; бейдж, очень узко' },
  shortA3: { id: 'quarantine.area.classShort.A3', defaultMessage: 'некарантинный', description: 'Класс вида (кратко): некарантинный вредный организм; бейдж, очень узко' },
  shortQuarantine: { id: 'quarantine.area.classShort.quarantine', defaultMessage: 'карантинный', description: 'Класс вида (кратко): карантинный объект; бейдж, очень узко' },
  shortHarmful: { id: 'quarantine.area.classShort.harmful', defaultMessage: 'прочий', description: 'Класс вида (кратко): прочий вредный организм; бейдж, очень узко' },
  shortUnspecified: { id: 'quarantine.area.classShort.unspecified', defaultMessage: 'не указан', description: 'Класс вида (кратко): класс не указан; бейдж, очень узко' },
  awaitControlFailed: { id: 'quarantine.area.awaiting.controlFailed', defaultMessage: 'контроль после срока не подтвердил устранение', description: 'Почему случай ждёт решения ответственного: контрольный замер после срока не подтвердил устранение; строчными' },
  awaitReadyToLift: { id: 'quarantine.area.awaiting.readyToLift', defaultMessage: 'устранено замером, красный не снят (запись до 08.10.2026) — снять вручную', description: 'Почему случай ждёт решения ответственного: замер показал устранение, нужно решить, снимать ли красный статус; строчными' },
  caseNoRequirement: { id: 'quarantine.area.case.noRequirement', defaultMessage: 'предписание не выдано', description: 'Состояние случая карантина: находка есть, предписание ещё не выдано; строчными' },
  caseOpen: { id: 'quarantine.area.case.open', defaultMessage: 'предписание выдано, отчёта нет', description: 'Состояние случая карантина: предписание выдано, отчёта хозяйства нет; строчными' },
  caseTreated: { id: 'quarantine.area.case.treated', defaultMessage: 'обработали, ждём контроля', description: 'Состояние случая карантина: хозяйство отчиталось об обработке, замера инспектора ещё нет (это не устранение); строчными' },
  caseDecision: { id: 'quarantine.area.case.decision', defaultMessage: 'ждёт решения службы', description: 'Состояние случая карантина: нужно решение ответственного; строчными' },
  caseNoAddressee: { id: 'quarantine.area.case.noAddressee', defaultMessage: 'адресат неизвестен — случай менеджеру', description: 'Состояние случая карантина: хозяйство-адресат предписания не установлено, случай разбирает менеджер; строчными' },
  caseEliminated: { id: 'quarantine.area.case.eliminated', defaultMessage: 'устранено замером', description: 'Состояние случая карантина: очаг устранён по замеру инспектора; строчными' },
  outsideNoPlacement: { id: 'quarantine.area.outside.noPlacement', defaultMessage: 'вне агроцикла: на дату выдачи размещения нет', description: 'Почему работа по предписанию вне агроцикла: на дату выдачи у поля нет размещения культуры; строчными' },
  outsideNoCycle: { id: 'quarantine.area.outside.noCycle', defaultMessage: 'вне агроцикла: агроцикл культуры не заведён', description: 'Почему работа по предписанию вне агроцикла: агроцикл культуры не заведён; строчными' },
  outsideCropsOverlap: { id: 'quarantine.area.outside.cropsOverlap', defaultMessage: 'ждёт выбора агроцикла: на поле два агроцикла', description: 'Работа по предписанию ждёт, пока менеджер выберет агроцикл: на поле одновременно два агроцикла; строчными' },
  outsidePartsDiffer: { id: 'quarantine.area.outside.partsDiffer', defaultMessage: 'вне агроцикла: части контура в разных агроциклах', description: 'Почему работа по предписанию вне агроцикла: части контура лежат в разных агроциклах; строчными' },
  haValue: { id: 'quarantine.area.haValue', defaultMessage: '{value} га', description: 'Гектары после числа; {value} — число, уже отформатированное' },
  partWhole: { id: 'quarantine.area.part.whole', defaultMessage: 'на контуре целиком', description: 'Где стоит сигнал: на всём контуре; строчными, начало строки «Поражённая часть»' },
  partPart: { id: 'quarantine.area.part.part', defaultMessage: 'на части контура', description: 'Где стоит сигнал: на части контура; строчными, начало строки «Поражённая часть»' },
  partNote: {
    id: 'quarantine.area.partNote',
    defaultMessage: '{where}{hasHa, select, yes { · под сигналом {ha}} other {}}{unspecified, select, yes { — поражённая часть не указана, взята вся обследованная единица} other {}}',
    description: 'Строка «Поражённая часть» в карточке контура; {where} — «на части контура» или «на контуре целиком», {ha} — площадь под сигналом, {unspecified} — yes, если часть не указана',
  },
});

const CLASS_LONG_MESSAGES = {
  A1: m.longA1,
  A2: m.longA2,
  A3: m.longA3,
  quarantine: m.longQuarantine,
  harmful: m.longHarmful,
  unspecified: m.longUnspecified,
};
const CLASS_SHORT_MESSAGES = {
  A1: m.shortA1,
  A2: m.shortA2,
  A3: m.shortA3,
  quarantine: m.shortQuarantine,
  harmful: m.shortHarmful,
  unspecified: m.shortUnspecified,
};

export type ObjectClass = NonNullable<QuarantineStatus['object_class']>;

export const CLASS_LONG: Record<ObjectClass, string> = lazyWords<ObjectClass>(CLASS_LONG_MESSAGES);
export const CLASS_SHORT: Record<ObjectClass, string> = lazyWords<ObjectClass>(CLASS_SHORT_MESSAGES);

/** Карантинный объект официального перечня — A1 или A2 (журнал №676); `quarantine` — прежнее значение. */
export function isQuarantineClass(value: string | null | undefined): boolean {
  return value === 'A1' || value === 'A2' || value === 'quarantine';
}

/** Подпись класса; `null` — у вида нет строки справочника, класс не из чего назвать. */
export function classShort(value: QuarantineStatus['object_class'] | undefined): string | null {
  return value ? CLASS_SHORT[value] ?? null : null;
}

export const AWAITING_WORD: Record<string, string> = lazyWords({
  control_failed: m.awaitControlFailed,
  ready_to_lift: m.awaitReadyToLift,
});

export const CASE_STATE_WORD: Record<string, string> = lazyWords({
  no_requirement: m.caseNoRequirement,
  open: m.caseOpen,
  treated_awaiting_control: m.caseTreated,
  awaiting_decision: m.caseDecision,
  no_addressee: m.caseNoAddressee,
  eliminated: m.caseEliminated,
});

/** Работа по предписанию «вне агроцикла» (№797): причина словами. */
export const OUTSIDE_CYCLE_WORD: Record<string, string> = lazyWords({
  no_placement: m.outsideNoPlacement,
  no_cycle: m.outsideNoCycle,
  parts_differ: m.outsidePartsDiffer,
  crops_overlap: m.outsideCropsOverlap,
});

/** Гектары для подписи: до сотых у малых значений, целые у крупных. */
export function haText(ha: number | null | undefined): string | null {
  if (ha === null || ha === undefined) return null;
  const digits = ha < 10 ? 2 : ha < 100 ? 1 : 0;
  return tr(m.haValue, { value: formatNum(ha, { maximumFractionDigits: digits }) });
}

/**
 * Итог «земли под сигналом» — точностью слоя метрик: десятые, целые от 100 га
 * (№1653). Строка виджета предписаний считает гектары из мкга, а плитка рядом
 * и ответ ИИ — метрикой, округлённой до десятых: «1,46 га» против «2 га» на
 * одной вкладке читались как разные числа. Строки отдельных частей — `haText`.
 */
export function landHaText(ha: number | null | undefined): string | null {
  if (ha === null || ha === undefined) return null;
  return tr(m.haValue, { value: landHaNum(ha) });
}

/** Число итога земли без единицы (KPI подписывает единицу сам) — правило `landHaText`. */
export function landHaNum(ha: number): string {
  return formatNum(ha, { maximumFractionDigits: ha < 100 ? 1 : 0 });
}

/**
 * Поражённая часть строки: на какой единице земли стоит сигнал и сколько там
 * под сигналом. «Часть не указана» — честно: взята вся обследованная единица.
 */
export function partNote(row: Pick<QuarantineStatus, 'on_part' | 'signal_ha' | 'part_unspecified'>): string | null {
  const ha = haText(row.signal_ha);
  const where = row.on_part ? tr(m.partPart) : tr(m.partWhole);
  if (ha === null && !row.on_part) return null;
  return tr(m.partNote, { where, hasHa: ha ? 'yes' : 'no', ha: ha ?? '', unspecified: row.part_unspecified ? 'yes' : 'no' });
}

/** Очередь «требует решения»: случаи со вскрытой причиной, крупные по земле — выше. */
export function decisionQueue(rows: WatchRow[]): WatchRow[] {
  return rows
    .filter((r) => r.status.awaiting_reason)
    .sort(
      (a, b) =>
        (b.status.signal_ha ?? 0) - (a.status.signal_ha ?? 0) || a.status.since.localeCompare(b.status.since),
    );
}

/**
 * Контуры, где хозяйство отчиталось по ЛЮБОЙ единице земли, а замера
 * инспектора ещё нет (№1651). Не только головная единица: контур с
 * обработанной частью и открытым случаем на другой части тоже ждёт контроля —
 * иначе очередь показывала 40 контуров при «42 случаях на 41 контуре».
 * Счёт сервера (`units_awaiting_control`), а при его отсутствии (фикстуры) —
 * состояние головного случая.
 */
export function awaitingControl(rows: WatchRow[]): WatchRow[] {
  return rows
    .filter((r) =>
      r.status.units_awaiting_control !== undefined
        ? r.status.units_awaiting_control > 0
        : r.status.case_state === 'treated_awaiting_control',
    )
    .sort((a, b) => a.status.since.localeCompare(b.status.since));
}

/**
 * Метрики района (журнал №672, S4 №960): все числа KPI — из слоя метрик, как и
 * у ИИ; клиент ничего не складывает. Каждое число несёт слой (basis) и единицу:
 * «га под сигналом» — земля, «га работ» — площадь работ, «контуров»/«находок» —
 * счёт. Пары «N из M» — две метрики рядом (красных из под сигналом).
 */
export const AREA_METRIC_IDS = [
  'quarantine.signal_area_ha',
  'quarantine.red_signal_area_ha',
  'quarantine.flagged_contours',
  'quarantine.red_contours',
  'quarantine.awaiting_decision',
  'quarantine.awaiting_decision_area_ha',
  'quarantine.eliminated_on_time_area_pct',
  'quarantine.eliminated_30d',
  'quarantine.overdue_contours',
  'quarantine.prescriptions_overdue_land_ha',
  'quarantine.treated_awaiting_control',
  'quarantine.treated_awaiting_control_land_ha',
  'quarantine.prescription_work_ha',
  'quarantine.no_addressee',
  'quarantine.outside_cycle',
] as const;
